import {
  FormControl,
  ListSubheader,
  ListSubheaderProps,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";

import { useState } from "react";
import useIsMobile from "../hooks/useIsMobile";
import { SxProps } from "@mui/system";
import Radio from "./Radio";
import Checkbox from "./Checkbox";
import Select from "./Select";

// See note here: https://mui.com/material-ui/react-select/#grouping
const MyListSubheader = (
  props: ListSubheaderProps & { muiSkipListHighlight: boolean },
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { muiSkipListHighlight: _muiSkipListHighlight, ...other } = props;
  return <ListSubheader {...other} />;
};

export type Option = {
  label: string;
  value: string;
  defaultChecked?: boolean;
};

type GroupArgs = {
  label: string;
  options: Option[];
  onChange: (s: Option[]) => void;
};

type Props = {
  groups: GroupArgs[];
  displayValue?: string | React.ReactNode;
  disabled?: boolean;
  radio?: boolean;
  multiple?: boolean;
  sx?: SxProps;
  sxSelect?: SxProps;
  sxPaper?: SxProps;
};

export default ({
  groups,
  displayValue,
  disabled = false,
  radio = false,
  multiple = true,
  sx,
  sxSelect,
  sxPaper,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<string[]>(
    groups.flatMap((g) =>
      g.options
        .filter((o) => o.defaultChecked)
        .map((o) => `${g.label}|${o.value}`),
    ),
  );

  const handleChange = (event: SelectChangeEvent<typeof values>) => {
    const {
      target: { value },
    } = event;
    let updatedValues = typeof value === "string" ? value.split(",") : value;

    // Handle radio button behavior - only one selection allowed
    if (radio) {
      const newEntries = updatedValues.filter((v) => values.indexOf(v) < 0);
      if (newEntries.length > 0) {
        // User selected a new item, keep only that one
        updatedValues = newEntries;
      } else {
        // User deselected the current item, keep as is
        updatedValues = updatedValues;
      }
    }

    setValues(updatedValues);
    const groupValues = new Map<string, string[]>();
    for (const v of updatedValues) {
      const [groupLabel, val] = v.split("|", 2);
      groupValues.set(
        groupLabel,
        (groupValues.get(groupLabel) || []).concat(val),
      );
    }
    for (const group of groups) {
      group.onChange(
        (groupValues.get(group.label) || []).map(
          (v) => group.options.find((o) => o.value === v) as Option,
        ),
      );
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const isMobile = useIsMobile();

  return (
    <FormControl sx={{ cursor: "pointer", ...sx }}>
      <Select
        disabled={disabled}
        value={values}
        multiple={multiple}
        displayEmpty
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        onClick={(e: React.MouseEvent<HTMLDivElement>) => {
          // Check if the click is exactly on the element with the event listener
          if (e.target === e.currentTarget) {
            setOpen(!open);
          }
        }}
        renderValue={() => (
          <Typography component="span" variant="body" fontWeight="500">
            {displayValue}
          </Typography>
        )}
        onChange={handleChange}
        sx={{
          ...sxSelect,
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              marginTop: "8px",
              borderRadius: "12px",
              border: "2px solid var(--Button-Colors-Gray-gray-3, #EAEBEC)",
              background: "var(--White, #FFF)",
              display: "flex",
              padding: isMobile ? "20px" : "16px 24px 8px 20px",
              flexDirection: "column",
              alignItems: "flex-start",
              boxShadow: "none",
              ul: {
                padding: 0,
                margin: 0,
                li: {
                  paddingLeft: "0px",
                  minWidth: "150px",
                  "&.Mui-selected, &:hover": {
                    backgroundColor: "transparent",
                  },
                  "&:last-child": {
                    marginBottom: "0px",
                  },
                },
              },
              ...sxPaper,
            },
          },
        }}
      >
        {groups.map((g, i) => {
          return [
            <MyListSubheader
              muiSkipListHighlight
              key={g.label}
              sx={{
                marginBottom: "4px",
                ...(i !== 0
                  ? {
                      borderTop: "1px solid #D4D4D4",
                      paddingTop: "12px",
                    }
                  : {}),
              }}
            >
              <Typography variant="bodyHeavy">{g.label}</Typography>
            </MyListSubheader>,
            ...g.options.map((o, idx) => (
              <MenuItem
                key={o.value}
                value={`${g.label}|${o.value}`}
                disableRipple={true}
                disableTouchRipple={true}
                sx={{
                  padding: 0,
                  "&:hover": { backgroundColor: "transparent" },
                  "&.Mui-selected:hover": { backgroundColor: "transparent" },
                  ...(idx === g.options.length - 1 && { paddingBottom: "8px" }),
                }}
              >
                {radio ? (
                  <Radio
                    size="small"
                    checked={values.indexOf(`${g.label}|${o.value}`) > -1}
                  />
                ) : (
                  <Checkbox
                    checked={values.indexOf(`${g.label}|${o.value}`) > -1}
                  />
                )}
                <ListItemText primary={o.label} />
              </MenuItem>
            )),
          ];
        })}
      </Select>
    </FormControl>
  );
};
